import React, { useEffect, useState } from 'react';
import { ImageOff, Loader2 } from 'lucide-react';
import { fetchImageObjectUrl } from '@/api/base44Client';

/**
 * An <img> for a picture that lives behind the API's bearer auth.
 *
 * Badge and person photos are R2 objects served by GET /api/images/..., which
 * requires an Authorization header. A browser does not send one for a plain
 * <img src>, so that request comes back 401 and the browser draws a broken
 * image icon. This fetches the bytes with the token, turns them into an object
 * URL, and hands that to a real <img>.
 *
 * The object URL is revoked on unmount. Without that, every scroll past a
 * contact card would leak a few hundred KB of blob for the life of the tab,
 * which on a long list on a phone adds up quickly.
 *
 * A token in the query string would avoid all of this, and was rejected: it
 * would land in access logs and browser history, and these are photographs of
 * real people.
 */
export default function AuthedImage({ src, alt = '', className = '', fallbackClassName = '' }) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let created = null;

    setObjectUrl(null);
    setFailed(false);

    if (!src) return undefined;

    fetchImageObjectUrl(src)
      .then((url) => {
        if (cancelled) {
          // Unmounted while the fetch was in flight. Nothing will ever render
          // this, so release it here instead of leaking it.
          URL.revokeObjectURL(url);
          return;
        }
        created = url;
        setObjectUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [src]);

  if (!src) return null;

  if (failed) {
    return (
      <div
        className={`flex items-center justify-center bg-muted text-muted-foreground ${fallbackClassName || className}`}
        title="This photo could not be loaded"
      >
        <ImageOff className="w-4 h-4" />
      </div>
    );
  }

  if (!objectUrl) {
    return (
      <div className={`flex items-center justify-center bg-muted ${fallbackClassName || className}`}>
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return <img src={objectUrl} alt={alt} className={className} />;
}
